/**
 * Client-side court-access envelopes and period wrapping.
 *
 * Recovery purposes, the recovery vault domain, and FARFAR-SSS-1 identity
 * shares are never valid here. Period access keys are independent random
 * values; reconstructing the access root does not derive next week's key.
 */

import {
  base64UrlToBytes,
  bytesToBase64Url,
  bytesToUtf8,
  randomBytes,
  utf8ToBytes
} from './bytes.ts';
import {
  combineSecretBytes,
  ShamirByteShare,
  splitSecretBytes
} from './shamir.ts';

export const ACCESS_PROTOCOL_VERSION = 1 as const;
export const ACCESS_PROTOCOL_DOMAIN = 'court-access' as const;
export const ACCESS_SEAL_VERSION = 1 as const;
export const ACCESS_SEAL_SUITE = 'P256-HKDF-SHA256-A256GCM-ACCESS-V1' as const;
export const ACCESS_PURPOSE_SHARE = 'court-access-share' as const;
export const ACCESS_PURPOSE_SESSION_SHARE = 'court-access-session-share' as const;
export const ACCESS_THRESHOLD = 2 as const;
export const ACCESS_CUSTODIAN_COUNT = 3 as const;
export const ACCESS_KEY_BYTES = 32 as const;
export const ACCESS_SHARE_VERSION = 1 as const;
export const ACCESS_WRAP_ENVELOPE_VERSION = 1 as const;
export const ACCESS_WRAP_ALGORITHM = 'A256GCM' as const;
export const ACCESS_PERIOD_WRAP_DOMAIN = 'farfar-access-period-wrap-v1' as const;
export const ACCESS_THREAD_EPOCH_WRAP_DOMAIN = 'farfar-access-thread-epoch-wrap-v1' as const;
export const ACCESS_ROOT_DEVICE_WRAP_DOMAIN = 'farfar-access-root-device-wrap-v1' as const;
export const ACCESS_SHARE_SEAL_DOMAIN = 'farfar-access-share-seal-v1' as const;
export const ACCESS_SHARE_KDF_DOMAIN = 'farfar-access-share-kdf-v1' as const;

export const ACCESS_CUSTODIAN_ROLES = ['directorate', 'depot', 'provider'] as const;
export type AccessCustodianRole = (typeof ACCESS_CUSTODIAN_ROLES)[number];

const AES_GCM_IV_BYTES = 12;
const EPOCH_ID_BYTES = 16;
const SEAL_ID_BYTES = 16;
const HKDF_SALT_BYTES = 32;
const MAX_SEALED_PLAINTEXT_BYTES = 16 * 1024;
const MAX_ID_LENGTH = 128;
const ECDH_P256 = { name: 'ECDH', namedCurve: 'P-256' } as const;
const ISO_WEEK_PERIOD_ID = /^(\d{4})-W(0[1-9]|[1-4]\d|5[0-3])(?:-r([2-9]|[1-9]\d))?$/;
const RECOVERY_VAULT_DOMAIN = 'farfar-adult-vault-wrap-v1';

export type AccessCryptoErrorCode =
  | 'INVALID_INPUT'
  | 'CONTEXT_MISMATCH'
  | 'AUTHENTICATION_FAILED'
  | 'INSUFFICIENT_SHARES';

export class AccessCryptoError extends Error {
  readonly code: AccessCryptoErrorCode;

  constructor(code: AccessCryptoErrorCode, message: string) {
    super(message);
    this.name = 'AccessCryptoError';
    this.code = code;
  }
}

export type AccessRootSharePackage = {
  shareVersion: typeof ACCESS_SHARE_VERSION;
  protocolVersion: typeof ACCESS_PROTOCOL_VERSION;
  protocolDomain: typeof ACCESS_PROTOCOL_DOMAIN;
  purpose: typeof ACCESS_PURPOSE_SHARE;
  index: number;
  threshold: typeof ACCESS_THRESHOLD;
  total: typeof ACCESS_CUSTODIAN_COUNT;
  shareValue: string;
};

type AccessWrapHeader = {
  envelopeVersion: typeof ACCESS_WRAP_ENVELOPE_VERSION;
  algorithm: typeof ACCESS_WRAP_ALGORITHM;
  protocolVersion: typeof ACCESS_PROTOCOL_VERSION;
  protocolDomain: typeof ACCESS_PROTOCOL_DOMAIN;
  domain: string;
  targetUserId: string;
  iv: string;
};

export type AccessPeriodEnvelope = AccessWrapHeader & {
  domain: typeof ACCESS_PERIOD_WRAP_DOMAIN;
  periodId: string;
  ciphertext: string;
};

export type AccessThreadEpochWrap = AccessWrapHeader & {
  domain: typeof ACCESS_THREAD_EPOCH_WRAP_DOMAIN;
  periodId: string;
  epoch: number;
  epochId: string;
  ciphertext: string;
};

export type AccessRootDeviceEnvelope = AccessWrapHeader & {
  domain: typeof ACCESS_ROOT_DEVICE_WRAP_DOMAIN;
  ciphertext: string;
};

export type AccessPublicKeyJwk = {
  kty: 'EC';
  crv: 'P-256';
  x: string;
  y: string;
};

export type AccessKeyPair = {
  publicKeyJwk: AccessPublicKeyJwk;
  publicKeyFingerprint: string;
  privateKey: CryptoKey;
};

export type AccessShareSealContext = {
  purpose: typeof ACCESS_PURPOSE_SHARE;
  protocolDomain: typeof ACCESS_PROTOCOL_DOMAIN;
  protocolVersion: typeof ACCESS_PROTOCOL_VERSION;
  custodySetId: string;
  custodySetVersion: number;
  manifestHash: string;
  custodianId: string;
  role: AccessCustodianRole;
  index: number;
  custodyPublicKeyFingerprint: string;
  periodId: string;
};

export type AccessSessionShareSealContext = {
  purpose: typeof ACCESS_PURPOSE_SESSION_SHARE;
  protocolDomain: typeof ACCESS_PROTOCOL_DOMAIN;
  protocolVersion: typeof ACCESS_PROTOCOL_VERSION;
  custodySetId: string;
  custodySetVersion: number;
  manifestHash: string;
  custodianId: string;
  role: AccessCustodianRole;
  index: number;
  custodyPublicKeyFingerprint: string;
  periodId: string;
  orderId: string;
  sessionId: string;
  sessionPublicKeyFingerprint: string;
  signedOrderHash: string;
  expiresAt: string;
};

export type AccessSealContext = AccessShareSealContext | AccessSessionShareSealContext;

export type AccessSealedShare = {
  sealVersion: typeof ACCESS_SEAL_VERSION;
  protocolVersion: typeof ACCESS_PROTOCOL_VERSION;
  suite: typeof ACCESS_SEAL_SUITE;
  sealId: string;
  recipientPublicKeyFingerprint: string;
  ephemeralPublicKeyJwk: AccessPublicKeyJwk;
  hkdfSalt: string;
  iv: string;
  ciphertext: string;
  context: AccessSealContext;
};

export type SignedOrderPayload = {
  protocolDomain: typeof ACCESS_PROTOCOL_DOMAIN;
  protocolVersion: typeof ACCESS_PROTOCOL_VERSION;
  targetUserId: string;
  periodStart: string;
  periodEnd: string;
  expiresAt: string;
  corpusScope: string[];
  sessionPublicKeyFingerprint: string;
};

type JsonRecord = Record<string, unknown>;

function invalid(message: string): never {
  throw new AccessCryptoError('INVALID_INPUT', message);
}

function mismatch(message: string): never {
  throw new AccessCryptoError('CONTEXT_MISMATCH', message);
}

function authFailed(message: string): never {
  throw new AccessCryptoError('AUTHENTICATION_FAILED', message);
}

function asRecord(value: unknown, label: string): JsonRecord {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    invalid(`${label} must be an object`);
  }
  return value as JsonRecord;
}

function assertExactKeys(record: JsonRecord, keys: readonly string[], label: string): void {
  const expected = new Set(keys);
  for (const key of Object.keys(record)) {
    if (!expected.has(key)) invalid(`${label} contains unknown field "${key}"`);
  }
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
      invalid(`${label} is missing field "${key}"`);
    }
  }
}

function boundedString(value: unknown, label: string, maxLength = MAX_ID_LENGTH): string {
  if (
    typeof value !== 'string'
    || value.length < 1
    || value.length > maxLength
    || value.trim() !== value
    || /[\u0000-\u001f\u007f]/.test(value)
  ) {
    invalid(`${label} must be a bounded, non-empty string`);
  }
  return value;
}

function positiveSafeInteger(value: unknown, label: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 1) {
    invalid(`${label} must be a positive safe integer`);
  }
  return value as number;
}

function isUint8Array(value: unknown): value is Uint8Array {
  return (
    ArrayBuffer.isView(value)
    && Object.prototype.toString.call(value) === '[object Uint8Array]'
  );
}

function assertBytes(value: unknown, length: number, label: string): asserts value is Uint8Array {
  if (!isUint8Array(value) || value.length !== length) {
    invalid(`${label} must be exactly ${length} bytes`);
  }
}

function decodeBase64Url(value: unknown, label: string, expectedLength?: number): Uint8Array {
  if (typeof value !== 'string' || value.length === 0 || !/^[A-Za-z0-9_-]+$/.test(value)) {
    invalid(`${label} must be unpadded base64url`);
  }
  let decoded: Uint8Array;
  try {
    decoded = base64UrlToBytes(value);
  } catch {
    invalid(`${label} must be valid base64url`);
  }
  if (bytesToBase64Url(decoded) !== value) {
    decoded.fill(0);
    invalid(`${label} is not canonical base64url`);
  }
  if (expectedLength !== undefined && decoded.length !== expectedLength) {
    decoded.fill(0);
    invalid(`${label} must encode ${expectedLength} bytes`);
  }
  return decoded;
}

function rejectForeignCrypto(value: unknown, label: string): void {
  const record = asRecord(value, label);
  if (isRecoveryPurpose(record.purpose) || isRecoveryPurpose((record.context as JsonRecord | undefined)?.purpose)) {
    mismatch('Recovery seals cannot be used for court access');
  }
  if (record.domain === RECOVERY_VAULT_DOMAIN || record.suite === 'P256-HKDF-SHA256-A256GCM') {
    mismatch('Recovery seals cannot be used for court access');
  }
  if (typeof record.circleId === 'string' && record.protocolDomain !== ACCESS_PROTOCOL_DOMAIN) {
    mismatch('Recovery seals cannot be used for court access');
  }
  if (containsLegacyIdentityShare(record)) {
    mismatch('Legacy identity shares cannot be used for court access');
  }
}

export function isRecoveryPurpose(purpose: unknown): boolean {
  return purpose === 'initial-share-custody' || purpose === 'recovery-request-share';
}

export function isAccessPurpose(purpose: unknown): boolean {
  return purpose === ACCESS_PURPOSE_SHARE || purpose === ACCESS_PURPOSE_SESSION_SHARE;
}

export function isAccessWrapDomain(domain: unknown): boolean {
  return typeof domain === 'string' && domain.startsWith('farfar-access-');
}

export function containsLegacyIdentityShare(value: unknown, seen = new Set<unknown>()): boolean {
  if (typeof value === 'string') return value.includes('FARFAR-SSS-1');
  if (!value || typeof value !== 'object') return false;
  if (seen.has(value)) return true;
  seen.add(value);
  if (Array.isArray(value)) {
    return value.some((item) => containsLegacyIdentityShare(item, seen));
  }
  return Object.values(value).some((item) => containsLegacyIdentityShare(item, seen));
}

export function isoWeekPeriodId(date: Date = new Date()): string {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) {
    invalid('Period date must be a valid Date');
  }
  const utc = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  utc.setUTCDate(utc.getUTCDate() + 4 - (utc.getUTCDay() || 7));
  const isoYear = utc.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const week = Math.ceil((((utc.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${isoYear}-W${String(week).padStart(2, '0')}`;
}

export function validateIsoWeekPeriodId(value: unknown, label = 'periodId'): string {
  const periodId = boundedString(value, label, 16);
  if (!ISO_WEEK_PERIOD_ID.test(periodId)) {
    invalid(`${label} must be an ISO week id such as 2026-W37`);
  }
  return periodId;
}

export function baseIsoWeekPeriodId(periodId: string): string {
  return validateIsoWeekPeriodId(periodId).replace(/-r[1-9]\d*$/, '');
}

export function nextAccessPeriodId(periodId: string): string {
  const validated = validateIsoWeekPeriodId(periodId);
  const match = validated.match(/^(.*)-r(\d+)$/);
  if (!match) return `${validated}-r2`;
  return `${match[1]}-r${Number(match[2]) + 1}`;
}

export function isoWeekPeriodIdsInRange(periodStart: string, periodEnd: string): string[] {
  const start = new Date(periodStart);
  const end = new Date(periodEnd);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || !(end.getTime() > start.getTime())) {
    invalid('Period range must have periodEnd after periodStart');
  }
  const ids: string[] = [];
  const seen = new Set<string>();
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  while (cursor.getTime() < end.getTime()) {
    const id = isoWeekPeriodId(cursor);
    if (!seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    if (ids.length > 10) invalid('Period range covers too many ISO weeks');
  }
  if (ids.length < 1) invalid('Period range must cover at least one ISO week');
  return ids;
}

export function validateAccessSealContext(value: unknown): {
  purpose: typeof ACCESS_PURPOSE_SHARE | typeof ACCESS_PURPOSE_SESSION_SHARE;
  protocolDomain: typeof ACCESS_PROTOCOL_DOMAIN;
  protocolVersion: typeof ACCESS_PROTOCOL_VERSION;
} {
  const context = asRecord(value, 'Access seal context');
  if (isRecoveryPurpose(context.purpose)) {
    mismatch('Recovery seals cannot be used for court access');
  }
  if (containsLegacyIdentityShare(context)) {
    mismatch('Legacy identity shares cannot be used for court access');
  }
  if (!isAccessPurpose(context.purpose)) {
    invalid('Unsupported access seal context purpose');
  }
  if (context.protocolDomain !== ACCESS_PROTOCOL_DOMAIN) {
    mismatch('Access seal protocolDomain must be court-access');
  }
  if (context.protocolVersion !== ACCESS_PROTOCOL_VERSION) {
    invalid('Unsupported access protocol version');
  }
  return {
    purpose: context.purpose as typeof ACCESS_PURPOSE_SHARE | typeof ACCESS_PURPOSE_SESSION_SHARE,
    protocolDomain: ACCESS_PROTOCOL_DOMAIN,
    protocolVersion: ACCESS_PROTOCOL_VERSION
  };
}

export function validateAccessSealedShare(value: unknown): {
  suite: typeof ACCESS_SEAL_SUITE;
  context: ReturnType<typeof validateAccessSealContext>;
} {
  const envelope = asRecord(value, 'Sealed access share');
  if (containsLegacyIdentityShare(envelope)) {
    mismatch('Legacy identity shares cannot be used for court access');
  }
  if (isRecoveryPurpose((envelope.context as JsonRecord | undefined)?.purpose)) {
    mismatch('Recovery seals cannot be used for court access');
  }
  if (envelope.suite === 'P256-HKDF-SHA256-A256GCM') {
    mismatch('Recovery seals cannot be used for court access');
  }
  if (
    envelope.sealVersion !== ACCESS_SEAL_VERSION
    || envelope.protocolVersion !== ACCESS_PROTOCOL_VERSION
    || envelope.suite !== ACCESS_SEAL_SUITE
  ) {
    invalid('Unsupported sealed access share');
  }
  return {
    suite: ACCESS_SEAL_SUITE,
    context: validateAccessSealContext(envelope.context)
  };
}

export function generateAccessRoot(): Uint8Array {
  return randomBytes(ACCESS_KEY_BYTES);
}

export function generatePeriodAccessKey(): Uint8Array {
  return randomBytes(ACCESS_KEY_BYTES);
}

export function splitAccessRoot(accessRoot: Uint8Array): AccessRootSharePackage[] {
  assertBytes(accessRoot, ACCESS_KEY_BYTES, 'Access root');
  const rawShares = splitSecretBytes(
    accessRoot,
    ACCESS_CUSTODIAN_COUNT,
    ACCESS_THRESHOLD
  );
  return rawShares.map((rawShare) => {
    try {
      return {
        shareVersion: ACCESS_SHARE_VERSION,
        protocolVersion: ACCESS_PROTOCOL_VERSION,
        protocolDomain: ACCESS_PROTOCOL_DOMAIN,
        purpose: ACCESS_PURPOSE_SHARE,
        index: rawShare.index,
        threshold: ACCESS_THRESHOLD,
        total: ACCESS_CUSTODIAN_COUNT,
        shareValue: bytesToBase64Url(rawShare.value)
      };
    } finally {
      rawShare.value.fill(0);
    }
  });
}

export function validateAccessRootSharePackage(value: unknown): AccessRootSharePackage {
  rejectForeignCrypto(value, 'Access root share');
  const share = asRecord(value, 'Access root share');
  assertExactKeys(
    share,
    [
      'shareVersion',
      'protocolVersion',
      'protocolDomain',
      'purpose',
      'index',
      'threshold',
      'total',
      'shareValue'
    ],
    'Access root share'
  );
  if (
    share.shareVersion !== ACCESS_SHARE_VERSION
    || share.protocolVersion !== ACCESS_PROTOCOL_VERSION
    || share.protocolDomain !== ACCESS_PROTOCOL_DOMAIN
    || share.purpose !== ACCESS_PURPOSE_SHARE
  ) {
    mismatch('Access root shares must use the court-access purpose');
  }
  if (share.threshold !== ACCESS_THRESHOLD || share.total !== ACCESS_CUSTODIAN_COUNT) {
    invalid('Access root shares must be 2-of-3');
  }
  const index = positiveSafeInteger(share.index, 'Access root share index');
  if (index > ACCESS_CUSTODIAN_COUNT) invalid('Access root share index is out of range');
  decodeBase64Url(share.shareValue, 'Access root share value', ACCESS_KEY_BYTES).fill(0);
  return {
    shareVersion: ACCESS_SHARE_VERSION,
    protocolVersion: ACCESS_PROTOCOL_VERSION,
    protocolDomain: ACCESS_PROTOCOL_DOMAIN,
    purpose: ACCESS_PURPOSE_SHARE,
    index,
    threshold: ACCESS_THRESHOLD,
    total: ACCESS_CUSTODIAN_COUNT,
    shareValue: share.shareValue as string
  };
}

export function combineAccessRootShares(shareValues: unknown[]): Uint8Array {
  if (!Array.isArray(shareValues) || shareValues.length < ACCESS_THRESHOLD) {
    throw new AccessCryptoError('INSUFFICIENT_SHARES', 'Court access needs two of three shares');
  }
  const packages = shareValues.map((item) => validateAccessRootSharePackage(item));
  const seen = new Set<number>();
  const rawShares: ShamirByteShare[] = packages.map((item) => {
    if (seen.has(item.index)) invalid('Duplicate access root share index');
    seen.add(item.index);
    return {
      index: item.index,
      value: decodeBase64Url(item.shareValue, 'Access root share value', ACCESS_KEY_BYTES)
    };
  });
  try {
    return combineSecretBytes(rawShares, ACCESS_THRESHOLD);
  } finally {
    for (const share of rawShares) share.value.fill(0);
  }
}

async function importAccessAesKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    raw,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

function wrapAad(header: Record<string, unknown>): Uint8Array {
  return utf8ToBytes(JSON.stringify(header));
}

async function aesWrap(
  wrappingKey: CryptoKey,
  plaintext: Uint8Array,
  header: Record<string, unknown>
): Promise<string> {
  const iv = decodeBase64Url(header.iv, 'Access wrap IV', AES_GCM_IV_BYTES);
  const aad = wrapAad(header);
  try {
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, additionalData: aad, tagLength: 128 },
      wrappingKey,
      plaintext
    );
    return bytesToBase64Url(new Uint8Array(ciphertext));
  } finally {
    iv.fill(0);
    aad.fill(0);
  }
}

async function aesUnwrap(
  wrappingKey: CryptoKey,
  envelope: { iv: string; ciphertext: string },
  header: Record<string, unknown>,
  expectedBytes: number,
  label: string
): Promise<Uint8Array> {
  const iv = decodeBase64Url(envelope.iv, `${label} IV`, AES_GCM_IV_BYTES);
  const ciphertext = decodeBase64Url(envelope.ciphertext, `${label} ciphertext`, expectedBytes + 16);
  const aad = wrapAad(header);
  try {
    let plaintext: ArrayBuffer;
    try {
      plaintext = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv, additionalData: aad, tagLength: 128 },
        wrappingKey,
        ciphertext
      );
    } catch {
      authFailed(`${label} authentication failed`);
    }
    const bytes = new Uint8Array(plaintext);
    if (bytes.length !== expectedBytes) {
      bytes.fill(0);
      invalid(`${label} did not contain a ${expectedBytes}-byte key`);
    }
    return bytes;
  } finally {
    iv.fill(0);
    ciphertext.fill(0);
    aad.fill(0);
  }
}

function validateWrapHeader(
  value: unknown,
  domain: string,
  extraKeys: readonly string[],
  label: string
): JsonRecord {
  rejectForeignCrypto(value, label);
  const envelope = asRecord(value, label);
  assertExactKeys(
    envelope,
    [
      'envelopeVersion',
      'algorithm',
      'protocolVersion',
      'protocolDomain',
      'domain',
      'targetUserId',
      ...extraKeys,
      'iv',
      'ciphertext'
    ],
    label
  );
  if (
    envelope.envelopeVersion !== ACCESS_WRAP_ENVELOPE_VERSION
    || envelope.algorithm !== ACCESS_WRAP_ALGORITHM
    || envelope.protocolVersion !== ACCESS_PROTOCOL_VERSION
    || envelope.protocolDomain !== ACCESS_PROTOCOL_DOMAIN
  ) {
    invalid(`Unsupported ${label}`);
  }
  if (envelope.domain !== domain) {
    mismatch(`${label} domain must be ${domain}`);
  }
  boundedString(envelope.targetUserId, `${label} targetUserId`);
  decodeBase64Url(envelope.iv, `${label} IV`, AES_GCM_IV_BYTES).fill(0);
  decodeBase64Url(envelope.ciphertext, `${label} ciphertext`, ACCESS_KEY_BYTES + 16).fill(0);
  return envelope;
}

export function validateAccessPeriodEnvelope(value: unknown): AccessPeriodEnvelope {
  const envelope = validateWrapHeader(value, ACCESS_PERIOD_WRAP_DOMAIN, ['periodId'], 'Access period envelope');
  return {
    envelopeVersion: ACCESS_WRAP_ENVELOPE_VERSION,
    algorithm: ACCESS_WRAP_ALGORITHM,
    protocolVersion: ACCESS_PROTOCOL_VERSION,
    protocolDomain: ACCESS_PROTOCOL_DOMAIN,
    domain: ACCESS_PERIOD_WRAP_DOMAIN,
    targetUserId: envelope.targetUserId as string,
    periodId: validateIsoWeekPeriodId(envelope.periodId, 'Access periodId'),
    iv: envelope.iv as string,
    ciphertext: envelope.ciphertext as string
  };
}

export function validateAccessThreadEpochWrap(value: unknown): AccessThreadEpochWrap {
  const envelope = validateWrapHeader(
    value,
    ACCESS_THREAD_EPOCH_WRAP_DOMAIN,
    ['periodId', 'epoch', 'epochId'],
    'Access thread-epoch wrap'
  );
  return {
    envelopeVersion: ACCESS_WRAP_ENVELOPE_VERSION,
    algorithm: ACCESS_WRAP_ALGORITHM,
    protocolVersion: ACCESS_PROTOCOL_VERSION,
    protocolDomain: ACCESS_PROTOCOL_DOMAIN,
    domain: ACCESS_THREAD_EPOCH_WRAP_DOMAIN,
    targetUserId: envelope.targetUserId as string,
    periodId: validateIsoWeekPeriodId(envelope.periodId, 'Access thread periodId'),
    epoch: positiveSafeInteger(envelope.epoch, 'Access thread epoch'),
    epochId: bytesToBase64Url(decodeBase64Url(envelope.epochId, 'Access thread epochId', EPOCH_ID_BYTES)),
    iv: envelope.iv as string,
    ciphertext: envelope.ciphertext as string
  };
}

export function validateAccessRootDeviceEnvelope(value: unknown): AccessRootDeviceEnvelope {
  const envelope = validateWrapHeader(
    value,
    ACCESS_ROOT_DEVICE_WRAP_DOMAIN,
    [],
    'Access root device envelope'
  );
  return {
    envelopeVersion: ACCESS_WRAP_ENVELOPE_VERSION,
    algorithm: ACCESS_WRAP_ALGORITHM,
    protocolVersion: ACCESS_PROTOCOL_VERSION,
    protocolDomain: ACCESS_PROTOCOL_DOMAIN,
    domain: ACCESS_ROOT_DEVICE_WRAP_DOMAIN,
    targetUserId: envelope.targetUserId as string,
    iv: envelope.iv as string,
    ciphertext: envelope.ciphertext as string
  };
}

function periodHeader(envelope: Omit<AccessPeriodEnvelope, 'ciphertext'>): Record<string, unknown> {
  return {
    domain: envelope.domain,
    envelopeVersion: envelope.envelopeVersion,
    algorithm: envelope.algorithm,
    protocolVersion: envelope.protocolVersion,
    protocolDomain: envelope.protocolDomain,
    targetUserId: envelope.targetUserId,
    periodId: envelope.periodId,
    iv: envelope.iv
  };
}

function threadHeader(envelope: Omit<AccessThreadEpochWrap, 'ciphertext'>): Record<string, unknown> {
  return {
    domain: envelope.domain,
    envelopeVersion: envelope.envelopeVersion,
    algorithm: envelope.algorithm,
    protocolVersion: envelope.protocolVersion,
    protocolDomain: envelope.protocolDomain,
    targetUserId: envelope.targetUserId,
    periodId: envelope.periodId,
    epoch: envelope.epoch,
    epochId: envelope.epochId,
    iv: envelope.iv
  };
}

function rootDeviceHeader(envelope: Omit<AccessRootDeviceEnvelope, 'ciphertext'>): Record<string, unknown> {
  return {
    domain: envelope.domain,
    envelopeVersion: envelope.envelopeVersion,
    algorithm: envelope.algorithm,
    protocolVersion: envelope.protocolVersion,
    protocolDomain: envelope.protocolDomain,
    targetUserId: envelope.targetUserId,
    iv: envelope.iv
  };
}

export async function wrapPeriodAccessKey(
  periodAccessKey: Uint8Array,
  accessRoot: Uint8Array,
  targetUserId: string,
  periodId: string
): Promise<AccessPeriodEnvelope> {
  assertBytes(periodAccessKey, ACCESS_KEY_BYTES, 'Period access key');
  assertBytes(accessRoot, ACCESS_KEY_BYTES, 'Access root');
  const header: Omit<AccessPeriodEnvelope, 'ciphertext'> = {
    envelopeVersion: ACCESS_WRAP_ENVELOPE_VERSION,
    algorithm: ACCESS_WRAP_ALGORITHM,
    protocolVersion: ACCESS_PROTOCOL_VERSION,
    protocolDomain: ACCESS_PROTOCOL_DOMAIN,
    domain: ACCESS_PERIOD_WRAP_DOMAIN,
    targetUserId: boundedString(targetUserId, 'Access period targetUserId'),
    periodId: validateIsoWeekPeriodId(periodId),
    iv: bytesToBase64Url(randomBytes(AES_GCM_IV_BYTES))
  };
  const key = await importAccessAesKey(accessRoot);
  return {
    ...header,
    ciphertext: await aesWrap(key, periodAccessKey, periodHeader(header))
  };
}

export async function unwrapPeriodAccessKey(
  envelopeValue: unknown,
  accessRoot: Uint8Array,
  expected: { targetUserId: string; periodId: string }
): Promise<Uint8Array> {
  assertBytes(accessRoot, ACCESS_KEY_BYTES, 'Access root');
  const envelope = validateAccessPeriodEnvelope(envelopeValue);
  if (envelope.targetUserId !== expected.targetUserId || envelope.periodId !== expected.periodId) {
    mismatch('Access period envelope does not match the expected target and week');
  }
  const key = await importAccessAesKey(accessRoot);
  return aesUnwrap(
    key,
    envelope,
    periodHeader(envelope),
    ACCESS_KEY_BYTES,
    'Access period envelope'
  );
}

export async function wrapThreadEpochForPeriod(
  threadKey: Uint8Array,
  periodAccessKey: Uint8Array,
  binding: { targetUserId: string; periodId: string; epoch?: number }
): Promise<AccessThreadEpochWrap> {
  assertBytes(threadKey, ACCESS_KEY_BYTES, 'Thread key');
  assertBytes(periodAccessKey, ACCESS_KEY_BYTES, 'Period access key');
  const header: Omit<AccessThreadEpochWrap, 'ciphertext'> = {
    envelopeVersion: ACCESS_WRAP_ENVELOPE_VERSION,
    algorithm: ACCESS_WRAP_ALGORITHM,
    protocolVersion: ACCESS_PROTOCOL_VERSION,
    protocolDomain: ACCESS_PROTOCOL_DOMAIN,
    domain: ACCESS_THREAD_EPOCH_WRAP_DOMAIN,
    targetUserId: boundedString(binding.targetUserId, 'Access thread targetUserId'),
    periodId: validateIsoWeekPeriodId(binding.periodId),
    epoch: binding.epoch == null ? 1 : positiveSafeInteger(binding.epoch, 'Access thread epoch'),
    epochId: bytesToBase64Url(randomBytes(EPOCH_ID_BYTES)),
    iv: bytesToBase64Url(randomBytes(AES_GCM_IV_BYTES))
  };
  const key = await importAccessAesKey(periodAccessKey);
  return {
    ...header,
    ciphertext: await aesWrap(key, threadKey, threadHeader(header))
  };
}

export async function unwrapThreadEpochForPeriod(
  envelopeValue: unknown,
  periodAccessKey: Uint8Array,
  expected: { targetUserId: string; periodId: string }
): Promise<Uint8Array> {
  assertBytes(periodAccessKey, ACCESS_KEY_BYTES, 'Period access key');
  const envelope = validateAccessThreadEpochWrap(envelopeValue);
  if (envelope.targetUserId !== expected.targetUserId || envelope.periodId !== expected.periodId) {
    mismatch('Access thread-epoch wrap does not match the expected target and week');
  }
  const key = await importAccessAesKey(periodAccessKey);
  return aesUnwrap(
    key,
    envelope,
    threadHeader(envelope),
    ACCESS_KEY_BYTES,
    'Access thread-epoch wrap'
  );
}

export async function wrapAccessRootForDevice(
  accessRoot: Uint8Array,
  wrapKey: CryptoKey,
  targetUserId: string
): Promise<AccessRootDeviceEnvelope> {
  assertBytes(accessRoot, ACCESS_KEY_BYTES, 'Access root');
  const header: Omit<AccessRootDeviceEnvelope, 'ciphertext'> = {
    envelopeVersion: ACCESS_WRAP_ENVELOPE_VERSION,
    algorithm: ACCESS_WRAP_ALGORITHM,
    protocolVersion: ACCESS_PROTOCOL_VERSION,
    protocolDomain: ACCESS_PROTOCOL_DOMAIN,
    domain: ACCESS_ROOT_DEVICE_WRAP_DOMAIN,
    targetUserId: boundedString(targetUserId, 'Access root targetUserId'),
    iv: bytesToBase64Url(randomBytes(AES_GCM_IV_BYTES))
  };
  return {
    ...header,
    ciphertext: await aesWrap(wrapKey, accessRoot, rootDeviceHeader(header))
  };
}

export async function unwrapAccessRootForDevice(
  envelopeValue: unknown,
  wrapKey: CryptoKey,
  expectedUserId: string
): Promise<Uint8Array> {
  const envelope = validateAccessRootDeviceEnvelope(envelopeValue);
  if (envelope.targetUserId !== expectedUserId) {
    mismatch('Access root device envelope does not match this account');
  }
  return aesUnwrap(
    wrapKey,
    envelope,
    rootDeviceHeader(envelope),
    ACCESS_KEY_BYTES,
    'Access root device envelope'
  );
}

function canonicalPublicKeyJson(publicKey: AccessPublicKeyJwk): string {
  return JSON.stringify({
    crv: publicKey.crv,
    kty: publicKey.kty,
    x: publicKey.x,
    y: publicKey.y
  });
}

async function digestSha256(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return bytesToBase64Url(new Uint8Array(digest));
}

export function normalizeAccessPublicKey(value: unknown): AccessPublicKeyJwk {
  const jwk = asRecord(value, 'Access public key');
  if (hasPrivateJwkField(jwk)) {
    invalid('Private or symmetric key material is not allowed in an access public key');
  }
  if (jwk.kty !== 'EC' || jwk.crv !== 'P-256') {
    invalid('Access public key must be an EC P-256 key');
  }
  decodeBase64Url(jwk.x, 'Access public key x', 32).fill(0);
  decodeBase64Url(jwk.y, 'Access public key y', 32).fill(0);
  return {
    kty: 'EC',
    crv: 'P-256',
    x: jwk.x as string,
    y: jwk.y as string
  };
}

function hasPrivateJwkField(record: JsonRecord): boolean {
  return ['d', 'p', 'q', 'dp', 'dq', 'qi', 'oth', 'k'].some((key) => (
    Object.prototype.hasOwnProperty.call(record, key)
  ));
}

export async function fingerprintAccessPublicKey(value: unknown): Promise<string> {
  const publicKey = normalizeAccessPublicKey(value);
  const encoded = utf8ToBytes(canonicalPublicKeyJson(publicKey));
  try {
    return await digestSha256(encoded);
  } finally {
    encoded.fill(0);
  }
}

export async function generateAccessKeyPair(): Promise<AccessKeyPair> {
  const pair = (await crypto.subtle.generateKey(
    ECDH_P256,
    false,
    ['deriveBits']
  )) as CryptoKeyPair;
  if (pair.privateKey.extractable) {
    invalid('Browser generated an unexpectedly extractable access private key');
  }
  const publicKeyJwk = normalizeAccessPublicKey(await crypto.subtle.exportKey('jwk', pair.publicKey));
  return {
    publicKeyJwk,
    publicKeyFingerprint: await fingerprintAccessPublicKey(publicKeyJwk),
    privateKey: pair.privateKey
  };
}

function canonicalSerializeAccessContext(context: AccessSealContext): string {
  return JSON.stringify(context);
}

function validateAccessRole(value: unknown): AccessCustodianRole {
  if (value !== 'directorate' && value !== 'depot' && value !== 'provider') {
    invalid('Access context role must be directorate, depot, or provider');
  }
  return value;
}

function validateSha256Fingerprint(value: unknown, label: string): string {
  return bytesToBase64Url(decodeBase64Url(value, label, 32));
}

export function validateAccessShareSealContext(value: unknown): AccessShareSealContext {
  rejectForeignCrypto(value, 'Access share context');
  const context = asRecord(value, 'Access share context');
  if (isRecoveryPurpose(context.purpose)) {
    mismatch('Recovery seals cannot be used for court access');
  }
  if (context.purpose !== ACCESS_PURPOSE_SHARE) {
    invalid('Access share context purpose must be court-access-share');
  }
  assertExactKeys(
    context,
    [
      'purpose',
      'protocolDomain',
      'protocolVersion',
      'custodySetId',
      'custodySetVersion',
      'manifestHash',
      'custodianId',
      'role',
      'index',
      'custodyPublicKeyFingerprint',
      'periodId'
    ],
    'Access share context'
  );
  if (context.protocolDomain !== ACCESS_PROTOCOL_DOMAIN) {
    mismatch('Access seal protocolDomain must be court-access');
  }
  if (context.protocolVersion !== ACCESS_PROTOCOL_VERSION) {
    invalid('Unsupported access protocol version');
  }
  const role = validateAccessRole(context.role);
  const index = positiveSafeInteger(context.index, 'Access context index');
  if (index !== ACCESS_CUSTODIAN_ROLES.indexOf(role) + 1) {
    invalid('Access context index does not match role');
  }
  return {
    purpose: ACCESS_PURPOSE_SHARE,
    protocolDomain: ACCESS_PROTOCOL_DOMAIN,
    protocolVersion: ACCESS_PROTOCOL_VERSION,
    custodySetId: boundedString(context.custodySetId, 'Access custodySetId'),
    custodySetVersion: positiveSafeInteger(context.custodySetVersion, 'Access custodySetVersion'),
    manifestHash: validateSha256Fingerprint(context.manifestHash, 'Access manifestHash'),
    custodianId: boundedString(context.custodianId, 'Access custodianId'),
    role,
    index,
    custodyPublicKeyFingerprint: validateSha256Fingerprint(
      context.custodyPublicKeyFingerprint,
      'Access custody public-key fingerprint'
    ),
    periodId: validateIsoWeekPeriodId(context.periodId, 'Access share periodId')
  };
}

export function validateAccessSessionShareSealContext(value: unknown): AccessSessionShareSealContext {
  rejectForeignCrypto(value, 'Access session share context');
  const context = asRecord(value, 'Access session share context');
  if (isRecoveryPurpose(context.purpose)) {
    mismatch('Recovery seals cannot be used for court access');
  }
  if (context.purpose !== ACCESS_PURPOSE_SESSION_SHARE) {
    invalid('Access session share context purpose must be court-access-session-share');
  }
  assertExactKeys(
    context,
    [
      'purpose',
      'protocolDomain',
      'protocolVersion',
      'custodySetId',
      'custodySetVersion',
      'manifestHash',
      'custodianId',
      'role',
      'index',
      'custodyPublicKeyFingerprint',
      'periodId',
      'orderId',
      'sessionId',
      'sessionPublicKeyFingerprint',
      'signedOrderHash',
      'expiresAt'
    ],
    'Access session share context'
  );
  const share = validateAccessShareSealContext({
    purpose: ACCESS_PURPOSE_SHARE,
    protocolDomain: context.protocolDomain,
    protocolVersion: context.protocolVersion,
    custodySetId: context.custodySetId,
    custodySetVersion: context.custodySetVersion,
    manifestHash: context.manifestHash,
    custodianId: context.custodianId,
    role: context.role,
    index: context.index,
    custodyPublicKeyFingerprint: context.custodyPublicKeyFingerprint,
    periodId: context.periodId
  });
  const expiresAt = boundedString(context.expiresAt, 'Access session expiry', 32);
  if (new Date(expiresAt).toISOString() !== expiresAt) {
    invalid('Access session expiry must be a canonical UTC ISO timestamp');
  }
  return {
    ...share,
    purpose: ACCESS_PURPOSE_SESSION_SHARE,
    orderId: boundedString(context.orderId, 'Access orderId'),
    sessionId: boundedString(context.sessionId, 'Access sessionId'),
    sessionPublicKeyFingerprint: validateSha256Fingerprint(
      context.sessionPublicKeyFingerprint,
      'Access session public-key fingerprint'
    ),
    signedOrderHash: validateSha256Fingerprint(context.signedOrderHash, 'Access signedOrderHash'),
    expiresAt
  };
}

export function canonicalSignedOrderPayload(payload: SignedOrderPayload): string {
  return JSON.stringify({
    protocolDomain: ACCESS_PROTOCOL_DOMAIN,
    protocolVersion: ACCESS_PROTOCOL_VERSION,
    targetUserId: boundedString(payload.targetUserId, 'Signed order targetUserId'),
    periodStart: boundedString(payload.periodStart, 'Signed order periodStart', 32),
    periodEnd: boundedString(payload.periodEnd, 'Signed order periodEnd', 32),
    expiresAt: boundedString(payload.expiresAt, 'Signed order expiresAt', 32),
    corpusScope: payload.corpusScope,
    sessionPublicKeyFingerprint: validateSha256Fingerprint(
      payload.sessionPublicKeyFingerprint,
      'Signed order session public-key fingerprint'
    )
  });
}

export async function hashSignedOrderPayload(payload: SignedOrderPayload): Promise<string> {
  const canonical = utf8ToBytes(canonicalSignedOrderPayload(payload));
  try {
    return await digestSha256(canonical);
  } finally {
    canonical.fill(0);
  }
}

export async function verifyMockRegistrySignature(
  payload: SignedOrderPayload,
  publicKeyJwk: unknown,
  signature: unknown
): Promise<boolean> {
  const publicKey = normalizeAccessPublicKey(publicKeyJwk);
  const signatureBytes = decodeBase64Url(signature, 'Mock registry signature', 64);
  const canonical = utf8ToBytes(canonicalSignedOrderPayload(payload));
  try {
    const key = await crypto.subtle.importKey(
      'jwk',
      publicKey,
      { name: 'ECDSA', namedCurve: 'P-256' },
      false,
      ['verify']
    );
    return crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      key,
      signatureBytes,
      canonical
    );
  } catch {
    return false;
  } finally {
    signatureBytes.fill(0);
    canonical.fill(0);
  }
}

function sealedShareAad(envelope: Omit<AccessSealedShare, 'ciphertext'>): Uint8Array {
  return utf8ToBytes(JSON.stringify({
    domain: ACCESS_SHARE_SEAL_DOMAIN,
    sealVersion: envelope.sealVersion,
    protocolVersion: envelope.protocolVersion,
    suite: envelope.suite,
    sealId: envelope.sealId,
    recipientPublicKeyFingerprint: envelope.recipientPublicKeyFingerprint,
    ephemeralPublicKeyJwk: envelope.ephemeralPublicKeyJwk,
    hkdfSalt: envelope.hkdfSalt,
    iv: envelope.iv,
    context: JSON.parse(canonicalSerializeAccessContext(envelope.context))
  }));
}

async function sealKdfInfo(
  recipientFingerprint: string,
  ephemeralPublicKey: AccessPublicKeyJwk,
  context: AccessSealContext
): Promise<Uint8Array> {
  return utf8ToBytes(JSON.stringify({
    domain: ACCESS_SHARE_KDF_DOMAIN,
    protocolVersion: ACCESS_PROTOCOL_VERSION,
    suite: ACCESS_SEAL_SUITE,
    recipientPublicKeyFingerprint: recipientFingerprint,
    ephemeralPublicKeyFingerprint: await fingerprintAccessPublicKey(ephemeralPublicKey),
    context: JSON.parse(canonicalSerializeAccessContext(context))
  }));
}

async function importAccessP256PublicKey(value: unknown): Promise<CryptoKey> {
  const publicKey = normalizeAccessPublicKey(value);
  try {
    return await crypto.subtle.importKey('jwk', publicKey, ECDH_P256, false, []);
  } catch {
    invalid('Access public key is not a valid P-256 point');
  }
}

function assertAccessPrivateKey(key: CryptoKey): void {
  const algorithm = key && (key.algorithm as KeyAlgorithm & { namedCurve?: string });
  if (
    !key
    || key.type !== 'private'
    || !algorithm
    || algorithm.name !== 'ECDH'
    || algorithm.namedCurve !== 'P-256'
    || !key.usages.includes('deriveBits')
  ) {
    invalid('Access private key must be a P-256 ECDH deriveBits key');
  }
}

async function deriveAccessSealKey(
  privateKey: CryptoKey,
  publicKeyValue: unknown,
  salt: Uint8Array,
  info: Uint8Array
): Promise<CryptoKey> {
  assertAccessPrivateKey(privateKey);
  const publicKey = await importAccessP256PublicKey(publicKeyValue);
  const sharedBuffer = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: publicKey },
    privateKey,
    256
  );
  const shared = new Uint8Array(sharedBuffer);
  try {
    const hkdfKey = await crypto.subtle.importKey('raw', shared, 'HKDF', false, ['deriveKey']);
    return crypto.subtle.deriveKey(
      { name: 'HKDF', hash: 'SHA-256', salt, info },
      hkdfKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  } finally {
    shared.fill(0);
  }
}

export function validateAccessSealedShareEnvelope(value: unknown): AccessSealedShare {
  rejectForeignCrypto(value, 'Sealed access share');
  const envelope = asRecord(value, 'Sealed access share');
  assertExactKeys(
    envelope,
    [
      'sealVersion',
      'protocolVersion',
      'suite',
      'sealId',
      'recipientPublicKeyFingerprint',
      'ephemeralPublicKeyJwk',
      'hkdfSalt',
      'iv',
      'ciphertext',
      'context'
    ],
    'Sealed access share'
  );
  if (
    envelope.sealVersion !== ACCESS_SEAL_VERSION
    || envelope.protocolVersion !== ACCESS_PROTOCOL_VERSION
    || envelope.suite !== ACCESS_SEAL_SUITE
  ) {
    invalid('Unsupported sealed access share');
  }
  const contextRecord = asRecord(envelope.context, 'Sealed access share context');
  const context = contextRecord.purpose === ACCESS_PURPOSE_SESSION_SHARE
    ? validateAccessSessionShareSealContext(contextRecord)
    : validateAccessShareSealContext(contextRecord);
  const recipientPublicKeyFingerprint = validateSha256Fingerprint(
    envelope.recipientPublicKeyFingerprint,
    'Sealed access recipient fingerprint'
  );
  const expectedRecipient = context.purpose === ACCESS_PURPOSE_SHARE
    ? context.custodyPublicKeyFingerprint
    : context.sessionPublicKeyFingerprint;
  if (recipientPublicKeyFingerprint !== expectedRecipient) {
    invalid('Sealed access recipient does not match its context');
  }
  decodeBase64Url(envelope.sealId, 'Access sealId', SEAL_ID_BYTES).fill(0);
  decodeBase64Url(envelope.hkdfSalt, 'Access HKDF salt', HKDF_SALT_BYTES).fill(0);
  decodeBase64Url(envelope.iv, 'Sealed access IV', AES_GCM_IV_BYTES).fill(0);
  const ciphertext = decodeBase64Url(
    envelope.ciphertext,
    'Sealed access ciphertext',
    undefined
  );
  if (ciphertext.length < 17 || ciphertext.length > MAX_SEALED_PLAINTEXT_BYTES + 16) {
    ciphertext.fill(0);
    invalid('Sealed access ciphertext length is invalid');
  }
  ciphertext.fill(0);
  return {
    sealVersion: ACCESS_SEAL_VERSION,
    protocolVersion: ACCESS_PROTOCOL_VERSION,
    suite: ACCESS_SEAL_SUITE,
    sealId: envelope.sealId as string,
    recipientPublicKeyFingerprint,
    ephemeralPublicKeyJwk: normalizeAccessPublicKey(envelope.ephemeralPublicKeyJwk),
    hkdfSalt: envelope.hkdfSalt as string,
    iv: envelope.iv as string,
    ciphertext: envelope.ciphertext as string,
    context
  };
}

async function sealAccessPayload(
  plaintext: Uint8Array,
  recipientPublicKeyValue: unknown,
  contextValue: AccessSealContext
): Promise<AccessSealedShare> {
  if (!isUint8Array(plaintext) || plaintext.length < 1 || plaintext.length > MAX_SEALED_PLAINTEXT_BYTES) {
    invalid('Access sealed payload has an invalid length');
  }
  const recipientPublicKey = normalizeAccessPublicKey(recipientPublicKeyValue);
  const recipientPublicKeyFingerprint = await fingerprintAccessPublicKey(recipientPublicKey);
  const context = contextValue.purpose === ACCESS_PURPOSE_SESSION_SHARE
    ? validateAccessSessionShareSealContext(contextValue)
    : validateAccessShareSealContext(contextValue);
  const expectedRecipient = context.purpose === ACCESS_PURPOSE_SHARE
    ? context.custodyPublicKeyFingerprint
    : context.sessionPublicKeyFingerprint;
  if (expectedRecipient !== recipientPublicKeyFingerprint) {
    mismatch('Access seal recipient does not match the bound context');
  }

  const ephemeral = (await crypto.subtle.generateKey(
    ECDH_P256,
    false,
    ['deriveBits']
  )) as CryptoKeyPair;
  const ephemeralPublicKeyJwk = normalizeAccessPublicKey(
    await crypto.subtle.exportKey('jwk', ephemeral.publicKey)
  );
  const salt = randomBytes(HKDF_SALT_BYTES);
  const iv = randomBytes(AES_GCM_IV_BYTES);
  const sealId = randomBytes(SEAL_ID_BYTES);
  const header: Omit<AccessSealedShare, 'ciphertext'> = {
    sealVersion: ACCESS_SEAL_VERSION,
    protocolVersion: ACCESS_PROTOCOL_VERSION,
    suite: ACCESS_SEAL_SUITE,
    sealId: bytesToBase64Url(sealId),
    recipientPublicKeyFingerprint,
    ephemeralPublicKeyJwk,
    hkdfSalt: bytesToBase64Url(salt),
    iv: bytesToBase64Url(iv),
    context
  };
  const info = await sealKdfInfo(recipientPublicKeyFingerprint, ephemeralPublicKeyJwk, context);
  const aad = sealedShareAad(header);
  try {
    const key = await deriveAccessSealKey(ephemeral.privateKey, recipientPublicKey, salt, info);
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, additionalData: aad, tagLength: 128 },
      key,
      plaintext
    );
    return {
      ...header,
      ciphertext: bytesToBase64Url(new Uint8Array(ciphertext))
    };
  } finally {
    salt.fill(0);
    iv.fill(0);
    sealId.fill(0);
    info.fill(0);
    aad.fill(0);
  }
}

async function openAccessPayload(
  envelopeValue: unknown,
  recipientPrivateKey: CryptoKey,
  expectedContextValue: AccessSealContext
): Promise<Uint8Array> {
  const envelope = validateAccessSealedShareEnvelope(envelopeValue);
  const expectedContext = expectedContextValue.purpose === ACCESS_PURPOSE_SESSION_SHARE
    ? validateAccessSessionShareSealContext(expectedContextValue)
    : validateAccessShareSealContext(expectedContextValue);
  if (canonicalSerializeAccessContext(envelope.context) !== canonicalSerializeAccessContext(expectedContext)) {
    mismatch('Sealed access share context does not match the expected order');
  }
  const salt = decodeBase64Url(envelope.hkdfSalt, 'Access HKDF salt', HKDF_SALT_BYTES);
  const iv = decodeBase64Url(envelope.iv, 'Sealed access IV', AES_GCM_IV_BYTES);
  const ciphertext = decodeBase64Url(envelope.ciphertext, 'Sealed access ciphertext');
  const info = await sealKdfInfo(
    envelope.recipientPublicKeyFingerprint,
    envelope.ephemeralPublicKeyJwk,
    envelope.context
  );
  const aad = sealedShareAad({
    sealVersion: envelope.sealVersion,
    protocolVersion: envelope.protocolVersion,
    suite: envelope.suite,
    sealId: envelope.sealId,
    recipientPublicKeyFingerprint: envelope.recipientPublicKeyFingerprint,
    ephemeralPublicKeyJwk: envelope.ephemeralPublicKeyJwk,
    hkdfSalt: envelope.hkdfSalt,
    iv: envelope.iv,
    context: envelope.context
  });
  try {
    let key: CryptoKey;
    try {
      key = await deriveAccessSealKey(
        recipientPrivateKey,
        envelope.ephemeralPublicKeyJwk,
        salt,
        info
      );
    } catch (error) {
      if (error instanceof AccessCryptoError) throw error;
      authFailed('Sealed access share authentication failed');
    }
    try {
      const plaintext = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv, additionalData: aad, tagLength: 128 },
        key,
        ciphertext
      );
      return new Uint8Array(plaintext);
    } catch {
      authFailed('Sealed access share authentication failed');
    }
  } finally {
    salt.fill(0);
    iv.fill(0);
    ciphertext.fill(0);
    info.fill(0);
    aad.fill(0);
  }
}

function encodeSharePackage(share: AccessRootSharePackage): Uint8Array {
  const validated = validateAccessRootSharePackage(share);
  return utf8ToBytes(JSON.stringify(validated));
}

function decodeSharePackage(bytes: Uint8Array): AccessRootSharePackage {
  let parsed: unknown;
  try {
    parsed = JSON.parse(bytesToUtf8(bytes));
  } catch {
    invalid('Unsealed access share is not JSON');
  }
  return validateAccessRootSharePackage(parsed);
}

export async function sealAccessRootShare(
  share: AccessRootSharePackage,
  recipientPublicKey: unknown,
  context: AccessShareSealContext
): Promise<AccessSealedShare> {
  const plaintext = encodeSharePackage(share);
  try {
    return await sealAccessPayload(plaintext, recipientPublicKey, context);
  } finally {
    plaintext.fill(0);
  }
}

export async function openAccessRootShare(
  envelope: unknown,
  recipientPrivateKey: CryptoKey,
  expectedContext: AccessShareSealContext
): Promise<AccessRootSharePackage> {
  const plaintext = await openAccessPayload(envelope, recipientPrivateKey, expectedContext);
  try {
    return decodeSharePackage(plaintext);
  } finally {
    plaintext.fill(0);
  }
}

export async function rewrapAccessShareForSession(input: {
  custodyEnvelope: unknown;
  custodianPrivateKey: CryptoKey;
  custodyContext: AccessShareSealContext;
  sessionPublicKey: unknown;
  sessionContext: AccessSessionShareSealContext;
  signedOrder: {
    payload: SignedOrderPayload;
    registryPublicKeyJwk: unknown;
    registrySignature: unknown;
  };
}): Promise<AccessSealedShare> {
  const valid = await verifyMockRegistrySignature(
    input.signedOrder.payload,
    input.signedOrder.registryPublicKeyJwk,
    input.signedOrder.registrySignature
  );
  if (!valid) {
    mismatch('Mock registry signature does not match this session public key');
  }
  const expectedHash = await hashSignedOrderPayload(input.signedOrder.payload);
  if (expectedHash !== input.sessionContext.signedOrderHash) {
    mismatch('Session share is not bound to the signed mock order');
  }
  if (
    input.signedOrder.payload.sessionPublicKeyFingerprint
    !== input.sessionContext.sessionPublicKeyFingerprint
  ) {
    mismatch('Session share is not bound to the signed mock order');
  }
  const share = await openAccessRootShare(
    input.custodyEnvelope,
    input.custodianPrivateKey,
    input.custodyContext
  );
  const plaintext = encodeSharePackage(share);
  try {
    return await sealAccessPayload(plaintext, input.sessionPublicKey, input.sessionContext);
  } finally {
    plaintext.fill(0);
  }
}

export async function openSessionAccessShare(
  envelope: unknown,
  sessionPrivateKey: CryptoKey,
  expectedContext: AccessSessionShareSealContext
): Promise<AccessRootSharePackage> {
  const plaintext = await openAccessPayload(envelope, sessionPrivateKey, expectedContext);
  try {
    return decodeSharePackage(plaintext);
  } finally {
    plaintext.fill(0);
  }
}

export async function hashAccessManifest(manifest: {
  protocolVersion: number;
  protocolDomain: string;
  custodySetId: string;
  custodySetVersion: number;
  targetUserId: string;
  threshold: number;
  total: number;
  periodId: string;
  createdAt: string;
  custodians: Array<{
    custodianId: string;
    index: number;
    role: AccessCustodianRole;
    custodyPublicKeyFingerprint: string;
  }>;
}): Promise<string> {
  const encoded = utf8ToBytes(JSON.stringify(manifest));
  try {
    return await digestSha256(encoded);
  } finally {
    encoded.fill(0);
  }
}
